import { parseDate, type CalendarDate } from '@internationalized/date';
import clsx from 'clsx';
import { Calendar, ChevronDown } from 'lucide-react';
import { HSDatepicker } from 'preline';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '../buttons';

type CalendarDateLike = {
  toString: () => string;
};

type SingleDatePickerProps = {
  value?: CalendarDate | null;
  defaultValue?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  minValue?: CalendarDateLike | null;
  maxValue?: CalendarDateLike | null;
  isDisabled?: boolean;
  className?: string;
};

type PrelineDatepickerState = {
  selectedDates?: unknown;
  context?: {
    selectedDates?: unknown;
  };
};

type PrelineDatepickerClickEvent = {
  target: EventTarget | null;
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDateString = (value: string | undefined | null): value is string =>
  Boolean(value && ISO_DATE_PATTERN.test(value));

const getIsoFromCalendarDate = (value?: CalendarDateLike | null) => {
  const iso = value?.toString?.();
  return isIsoDateString(iso) ? iso : null;
};

const toUtcDate = (iso: string) => new Date(`${iso}T00:00:00.000Z`);

const formatDisplayDate = (value?: CalendarDateLike | null): string => {
  const iso = getIsoFromCalendarDate(value);
  if (!iso) return '-- --- ----';

  const date = toUtcDate(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** Clamps a single date to [minIso, maxIso] — no range, no ordering. */
const normalizeSingleValue = (
  value: CalendarDate | null,
  minIso: string | null,
  maxIso: string | null
): CalendarDate | null => {
  const iso = getIsoFromCalendarDate(value);
  if (!iso) return null;

  let bounded = iso;
  if (minIso && bounded < minIso) bounded = minIso;
  if (maxIso && bounded > maxIso) bounded = maxIso;

  return parseDate(bounded);
};

const extractSelectedDate = (input: unknown): string | null => {
  if (!Array.isArray(input)) return null;

  for (const entry of input) {
    if (typeof entry !== 'string') continue;
    const match = entry.match(/\d{4}-\d{2}-\d{2}/);
    if (match) return match[0];
  }

  return null;
};

const getMonthStart = (iso: string) => {
  const date = toUtcDate(iso);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
};

// Single-month view — unlike the range picker, there's no second month to
// reserve room for, so bounds clamp directly against min/max month.
const clampCalendarView = (
  selectedMonth: number,
  selectedYear: number,
  minIso: string | null,
  maxIso: string | null
): { selectedMonth: number; selectedYear: number } => {
  const selectedMonthStart = new Date(Date.UTC(selectedYear, selectedMonth, 1));
  const minMonthStart = minIso ? getMonthStart(minIso) : null;
  const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;

  if (minMonthStart && selectedMonthStart < minMonthStart) {
    return {
      selectedMonth: minMonthStart.getUTCMonth(),
      selectedYear: minMonthStart.getUTCFullYear(),
    };
  }

  if (maxMonthStart && selectedMonthStart > maxMonthStart) {
    return {
      selectedMonth: maxMonthStart.getUTCMonth(),
      selectedYear: maxMonthStart.getUTCFullYear(),
    };
  }

  return { selectedMonth, selectedYear };
};

const getInitialMonthView = (
  minIso: string | null,
  maxIso: string | null,
  selectedIso?: string | null
) => {
  const now = new Date();
  const currentMonthStart = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), 1)
  );
  const minMonthStart = minIso ? getMonthStart(minIso) : null;
  const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;

  let targetMonthStart = isIsoDateString(selectedIso)
    ? getMonthStart(selectedIso)
    : currentMonthStart;

  if (minMonthStart && targetMonthStart < minMonthStart) {
    targetMonthStart = minMonthStart;
  }
  if (maxMonthStart && targetMonthStart > maxMonthStart) {
    targetMonthStart = maxMonthStart;
  }

  return {
    selectedMonth: targetMonthStart.getUTCMonth(),
    selectedYear: targetMonthStart.getUTCFullYear(),
  };
};

const getCustomSelectStyles = () => ({
  shared: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex text-nowrap w-full cursor-pointer bg-transparent text-start text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
    dropdownVerticalFixedPlacement: 'bottom',
    dropdownScope: 'parent',
    dropdownClasses:
      'mt-2 z-50 w-28 min-w-28 max-h-60 p-1 space-y-0.5 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-300',
    optionClasses:
      'p-2 w-full text-sm text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg focus:outline-hidden focus:bg-slate-100',
    optionTemplate:
      '<div class="flex justify-between items-center w-full"><span data-title></span></div>',
  },
  // Fixed (not w-full) so the toggle's own width — and therefore the
  // header row's total width — stays constant regardless of the selected
  // month/year's character count. Without this, "May" vs. "September"
  // render at different intrinsic widths and the days-of-week row (fixed
  // width) drifts out of alignment with the day grid below it.
  months: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex justify-center overflow-hidden text-ellipsis text-nowrap w-28 cursor-pointer bg-transparent text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
  },
  years: {
    toggleClasses:
      'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-1 px-2 pe-6 flex justify-center overflow-hidden text-ellipsis text-nowrap w-20 cursor-pointer bg-transparent text-sm font-medium text-slate-700 focus:outline-hidden before:absolute before:inset-0 before:z-1',
  },
});

const SingleDatePicker = ({
  value,
  defaultValue = null,
  onChange,
  minValue = null,
  maxValue = null,
  isDisabled = false,
  className,
}: SingleDatePickerProps) => {
  const isControlled = value !== undefined;
  const minIso = getIsoFromCalendarDate(minValue);
  const maxIso = getIsoFromCalendarDate(maxValue);
  const [internalValue, setInternalValue] = useState<CalendarDate | null>(
    normalizeSingleValue(defaultValue, minIso, maxIso)
  );
  const committedValue = useMemo(
    () =>
      normalizeSingleValue(
        isControlled ? (value ?? null) : internalValue,
        minIso,
        maxIso
      ),
    [internalValue, isControlled, maxIso, minIso, value]
  );
  const [draftValue, setDraftValue] = useState<CalendarDate | null>(
    committedValue
  );
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties | undefined>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const datepickerRef = useRef<HSDatepicker | null>(null);
  const calendarObserverRef = useRef<MutationObserver | null>(null);

  const initialViewTargetIso = getIsoFromCalendarDate(committedValue);

  const destroyDatepicker = () => {
    const instance = datepickerRef.current;

    if (!instance) return;

    try {
      instance.destroy();
    } catch {
      // Preline can throw during teardown if its internal collection state is missing.
    } finally {
      datepickerRef.current = null;
    }
  };

  const syncCalendarSelection = (value: CalendarDate | null) => {
    const vanillaCalendar = (datepickerRef.current as any)?.vanillaCalendar as
      | {
          set: (
            nextState: { selectedDates: string[] },
            options: {
              dates: boolean;
              month: boolean;
              year: boolean;
              time: boolean;
            }
          ) => void;
        }
      | undefined;

    if (!vanillaCalendar) return;

    const iso = getIsoFromCalendarDate(value);

    vanillaCalendar.set(
      { selectedDates: iso ? [iso] : [] },
      {
        dates: true,
        month: true,
        year: true,
        time: false,
      }
    );
  };

  const updateDraftFromSelectedDates = (selectedDatesInput: unknown) => {
    const iso = extractSelectedDate(selectedDatesInput);

    if (!iso) {
      setDraftValue(null);
      return;
    }

    setDraftValue(normalizeSingleValue(parseDate(iso), minIso, maxIso));
  };

  useEffect(() => {
    if (isOpen) return;

    const committedIso = getIsoFromCalendarDate(committedValue);
    const draftIso = getIsoFromCalendarDate(draftValue);

    if (committedIso !== draftIso) {
      setDraftValue(committedValue);
    }
  }, [committedValue, draftValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) return;
      setDraftValue(committedValue);
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDraftValue(committedValue);
      setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [committedValue, isOpen]);

  useEffect(
    () => () => {
      destroyDatepicker();
    },
    []
  );

  useEffect(() => {
    if (isOpen) return;

    destroyDatepicker();

    if (calendarRef.current) {
      calendarRef.current.innerHTML = '';
    }
  }, [isOpen]);

  const syncDraftFromDatepicker = (state?: PrelineDatepickerState) => {
    updateDraftFromSelectedDates(
      state?.context?.selectedDates ??
        state?.selectedDates ??
        datepickerRef.current?.getCurrentState()?.selectedDates
    );
  };

  const applyCalendarElementOverrides = () => {
    const root = calendarRef.current;
    if (!root) return;

    root
      .querySelectorAll<HTMLElement>('[data-vc-date-disabled]')
      .forEach((cell) => {
        const button = cell.querySelector<HTMLButtonElement>('button');

        cell.style.pointerEvents = 'none';
        cell.style.background = 'transparent';
        cell.style.boxShadow = 'none';
        cell.style.borderColor = 'transparent';
        cell.style.color = '#D1D5DB';

        if (!button) return;

        button.style.pointerEvents = 'none';
        button.style.background = 'transparent';
        button.style.boxShadow = 'none';
        button.style.border = '0';
        button.style.outline = '0';
        button.style.color = '#D1D5DB';
        button.style.cursor = 'not-allowed';
      });

    root
      .querySelectorAll<HTMLElement>('[data-vc-date-today]')
      .forEach((cell) => {
        const button = cell.querySelector<HTMLButtonElement>('button');
        const isSelected = cell.hasAttribute('data-vc-date-selected');

        if (isSelected) {
          cell.style.background = 'transparent';
          cell.style.boxShadow = 'none';
          cell.style.color = '#fff';
        } else {
          cell.style.background = 'transparent';
          cell.style.boxShadow = 'none';
          cell.style.color = '#4f46e5';
        }

        if (!button) return;

        button.style.boxShadow = 'none';
        button.style.border = '0';
        button.style.outline = '0';
        if (isSelected) {
          button.style.background = '#155dfc';
          button.style.setProperty('color', '#fff', 'important');
        } else {
          button.style.background = 'transparent';
          button.style.color = '#4f46e5';
        }

        if (isSelected) {
          cell.style.setProperty('color', '#fff', 'important');
        }
      });
  };

  const scheduleCalendarElementOverrides = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(applyCalendarElementOverrides);
      requestAnimationFrame(updateArrowVisibility);
    });
  };

  const startCalendarObserver = () => {
    const root = calendarRef.current;
    if (!root) return;

    calendarObserverRef.current?.disconnect();
    calendarObserverRef.current = new MutationObserver(() => {
      enforceCalendarViewBounds();
      scheduleCalendarElementOverrides();
    });

    calendarObserverRef.current.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
    });
  };

  const enforceCalendarViewBounds = () => {
    const vanillaCalendar = (datepickerRef.current as any)?.vanillaCalendar as
      | {
          context?: {
            selectedMonth?: number;
            selectedYear?: number;
          };
          set: (
            nextState: { selectedMonth: number; selectedYear: number },
            options: {
              dates: boolean;
              month: boolean;
              year: boolean;
              time: boolean;
            }
          ) => void;
        }
      | undefined;

    const selectedMonth = vanillaCalendar?.context?.selectedMonth;
    const selectedYear = vanillaCalendar?.context?.selectedYear;

    if (
      !vanillaCalendar ||
      typeof selectedMonth !== 'number' ||
      typeof selectedYear !== 'number'
    ) {
      return;
    }

    const clamped = clampCalendarView(
      selectedMonth,
      selectedYear,
      minIso,
      maxIso
    );
    if (
      clamped.selectedMonth === selectedMonth &&
      clamped.selectedYear === selectedYear
    ) {
      return;
    }

    vanillaCalendar.set(clamped, {
      dates: false,
      month: true,
      year: true,
      time: false,
    });
  };

  const updateArrowVisibility = () => {
    const root = calendarRef.current;
    if (!root) return;

    const prevArrow = root.querySelector<HTMLElement>('[data-vc-arrow="prev"]');
    const nextArrow = root.querySelector<HTMLElement>('[data-vc-arrow="next"]');
    const selectedMonth = (datepickerRef.current as any)?.vanillaCalendar
      ?.context?.selectedMonth;
    const selectedYear = (datepickerRef.current as any)?.vanillaCalendar
      ?.context?.selectedYear;

    if (typeof selectedMonth !== 'number' || typeof selectedYear !== 'number') {
      return;
    }

    const visibleMonth = new Date(Date.UTC(selectedYear, selectedMonth, 1));
    const minMonthStart = minIso ? getMonthStart(minIso) : null;
    const maxMonthStart = maxIso ? getMonthStart(maxIso) : null;

    if (prevArrow) {
      const hidePrev = minMonthStart ? visibleMonth <= minMonthStart : false;
      prevArrow.style.visibility = hidePrev ? 'hidden' : 'visible';
      prevArrow.style.pointerEvents = hidePrev ? 'none' : 'auto';
    }

    if (nextArrow) {
      const hideNext = maxMonthStart ? visibleMonth >= maxMonthStart : false;
      nextArrow.style.visibility = hideNext ? 'hidden' : 'visible';
      nextArrow.style.pointerEvents = hideNext ? 'none' : 'auto';
    }
  };

  const updatePopoverPlacement = () => {
    if (!isOpen) {
      setPopoverStyle(undefined);
      return;
    }

    const triggerRect = wrapperRef.current?.getBoundingClientRect();
    if (!triggerRect) return;

    const popoverWidth = popoverRef.current?.offsetWidth ?? 0;
    const viewportMargin = 20;

    const preferredLeft = triggerRect.left;
    const maxLeft = window.innerWidth - viewportMargin - popoverWidth;
    const clampedLeft = Math.min(
      Math.max(preferredLeft, viewportMargin),
      Math.max(maxLeft, viewportMargin)
    );

    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - triggerRect.bottom - 12;
    const shouldFlipUp =
      spaceBelow < popoverHeight && triggerRect.top - 12 > popoverHeight;
    const top = shouldFlipUp
      ? Math.round(triggerRect.top - popoverHeight - 12)
      : Math.round(triggerRect.bottom + 12);

    setPopoverStyle({
      position: 'fixed',
      top: `${Math.max(top, viewportMargin)}px`,
      left: `${Math.round(clampedLeft)}px`,
      right: 'auto',
      zIndex: 80,
    });
  };

  const renderDatepicker = (nextValue: CalendarDate | null) => {
    const container = calendarRef.current;
    if (!container) return;

    destroyDatepicker();
    container.innerHTML = '';

    const iso = getIsoFromCalendarDate(nextValue);
    const selectedDates = iso ? [iso] : [];
    const initialView = getInitialMonthView(
      minIso,
      maxIso,
      iso ?? initialViewTargetIso
    );

    const instance = new HSDatepicker(container, {
      inputMode: false,
      type: 'default',
      mode: 'custom-select',
      displayMonthsCount: 1,
      selectionDatesMode: 'single',
      displayDatesOutside: false,
      dateMin: minIso ?? undefined,
      dateMax: maxIso ?? undefined,
      selectedTheme: 'light',
      applyUtilityClasses: true,
      selectedMonth: initialView.selectedMonth,
      selectedYear: initialView.selectedYear,
      styles: {
        customSelect: getCustomSelectStyles(),
      },
      templates: {
        arrowPrev:
          '<button data-vc-arrow="prev"><svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></svg></button>',
        arrowNext:
          '<button data-vc-arrow="next"><svg class="shrink-0 size-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg></button>',
      },
      onInit: (calendar: any) => {
        calendar.set(
          {
            selectedMonth: initialView.selectedMonth,
            selectedYear: initialView.selectedYear,
          },
          {
            dates: false,
            month: true,
            year: true,
            time: false,
          }
        );
        if (selectedDates.length) {
          calendar.set(
            { selectedDates },
            {
              dates: true,
              month: true,
              year: true,
              time: false,
            }
          );
        }
        enforceCalendarViewBounds();
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
      onClickDate: (
        calendar: PrelineDatepickerState,
        event?: PrelineDatepickerClickEvent
      ) => {
        const target = event?.target;
        const clickedElement = target instanceof HTMLElement ? target : null;
        const clickedDateCell = clickedElement?.closest('[data-vc-date]');
        const clickedIso = clickedDateCell?.getAttribute('data-vc-date');
        const isDisabledDate = Boolean(
          clickedDateCell?.getAttribute('data-vc-date-disabled')
        );
        const isOutOfAllowedRange = Boolean(
          clickedIso &&
            ((minIso && clickedIso < minIso) || (maxIso && clickedIso > maxIso))
        );

        if (isDisabledDate || isOutOfAllowedRange) {
          requestAnimationFrame(() => {
            syncCalendarSelection(draftValue);
            scheduleCalendarElementOverrides();
            updatePopoverPlacement();
          });
          return;
        }

        syncDraftFromDatepicker(calendar);
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
      onChangeToInput: (calendar: PrelineDatepickerState) => {
        syncDraftFromDatepicker(calendar);
        enforceCalendarViewBounds();
        scheduleCalendarElementOverrides();
        updatePopoverPlacement();
      },
    });

    datepickerRef.current = instance;
  };

  useEffect(() => {
    if (!isOpen) return;
    renderDatepicker(draftValue);
    startCalendarObserver();
    requestAnimationFrame(updatePopoverPlacement);
    scheduleCalendarElementOverrides();
    return () => {
      calendarObserverRef.current?.disconnect();
      calendarObserverRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleViewportChange = () => {
      updatePopoverPlacement();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen]);

  const applyDraft = () => {
    const normalized = normalizeSingleValue(draftValue, minIso, maxIso);
    if (!normalized) return;

    if (!isControlled) {
      setInternalValue(normalized);
    }

    onChange?.(normalized);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDraftValue(committedValue);
    setIsOpen(false);
  };

  const handleTriggerMouseDown = (
    event: ReactMouseEvent<HTMLButtonElement>
  ) => {
    if (event.button !== 0 || isDisabled) return;
    event.preventDefault();
    setDraftValue(committedValue);
    setIsOpen((current) => !current);
  };

  const canApply = Boolean(draftValue);

  return (
    <div ref={wrapperRef} className={clsx('relative', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onMouseDown={handleTriggerMouseDown}
        className={clsx(
          'group flex min-h-[46px] items-center gap-3 rounded-lg border border-transparent bg-indigo-100 px-4 py-3 text-left shadow-xs transition-colors',
          isDisabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:bg-indigo-500',
          isOpen ? 'bg-indigo-100' : ''
        )}
      >
        <Calendar className="size-8 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
        <span className="min-w-0 flex-1 text-sm font-medium text-indigo-800 transition-colors group-hover:text-white">
          {formatDisplayDate(committedValue)}
        </span>
        <ChevronDown className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
      </button>

      {isOpen ? (
        <div
          ref={popoverRef}
          className="date-picker-popover absolute top-[calc(100%+12px)] z-80 overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-[0_16px_38px_rgba(15,23,42,0.12)]"
          style={popoverStyle}
        >
          <div className="min-w-0 bg-white">
            <div className="relative flex justify-center overflow-x-auto p-5">
              <div
                ref={calendarRef}
                // The month/year select toggles are fixed-width (see
                // getCustomSelectStyles above), but pin the header row
                // itself too so its grid track can't be re-sized by
                // Preline/vanilla-calendar internals based on the
                // currently selected label's text length.
                className={clsx(
                  'date-picker-calendar-shell w-75',
                  '**:data-[vc=header]:mx-auto **:data-[vc=header]:w-52'
                )}
              />
            </div>

            <div className="flex gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-5 md:px-5">
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
                <span>{formatDisplayDate(draftValue)}</span>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="white"
                  className="py-2.5! px-3!"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  className="py-2.5! px-3!"
                  onClick={applyDraft}
                  disabled={!canApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SingleDatePicker;
